'use client'

import { Box, Typography } from '@mui/material'
import { appPaths } from '@config'
import { Link } from '@assets-components'
import { Image } from '@media-components'
import type { StandardCardData } from '@components/common'
import { getStyles } from '@utils/styles'
import defaultStyles from './styles'

type SearchBlogCardProps = StandardCardData

function formatReadTime(timeRead?: string) {
  const value = timeRead?.trim()
  if (!value) return undefined
  if (/min/i.test(value)) return value
  return `${value} min read`
}

export function SearchBlogCard({
  title,
  href,
  imageData,
  blogData,
}: SearchBlogCardProps) {
  const styles = getStyles(defaultStyles)
  const isLink = Boolean(href)
  const authorHref = blogData?.authorSlug
    ? `${appPaths.authors}/${blogData.authorSlug}`
    : undefined
  const readTime = formatReadTime(blogData?.timeRead)
  const metaParts = [readTime, blogData?.date].filter(Boolean)

  return (
    <Box
      {...styles('card')}
      {...(isLink
        ? {
            component: Link,
            href,
          }
        : {})}
    >
      {imageData ? (
        <Image
          {...imageData}
          {...styles('image')}
          alt={imageData.alt || title}
        />
      ) : null}
      <Box {...styles('content')}>
        {title ? (
          <Typography
            variant="body1.600"
            component="h3"
            color="text.primary"
            {...styles('title')}
          >
            {title}
          </Typography>
        ) : null}
        <Box {...styles('meta')}>
          {blogData?.authorName ? (
            <Box
              {...styles('author')}
              {...(authorHref
                ? {
                    component: 'a',
                    href: authorHref,
                    onClick: (event: { stopPropagation: () => void }) =>
                      event.stopPropagation(),
                  }
                : {})}
            >
              {blogData.authorImage ? (
                <Image
                  {...blogData.authorImage}
                  {...styles('avatar')}
                  alt={blogData.authorImage.alt || blogData.authorName}
                />
              ) : null}
              <Typography variant="body3.500" color="text.primary">
                {blogData.authorName}
              </Typography>
            </Box>
          ) : null}
          {metaParts.map((part) => (
            <Typography
              key={part}
              variant="body3.500"
              color="text.secondary"
              component="span"
            >
              • {part}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
